import { FgdsProject } from "@domain/project/fgds-project";
import { ProjectRelatedService } from "@domain/project/project-related-service";

export type FgdsServiceKey = "assembly" | "installation" | "documentation" | "engineering" | "additionalServices";

export interface FgdsServiceDefinition {
  key: FgdsServiceKey;
  label: () => string;
  partNumber: string;
}

export interface FgdsProjectServiceEntry {
  definition: FgdsServiceDefinition;
  service: ProjectRelatedService;
}

export interface PositionedFgdsProjectServiceEntry extends FgdsProjectServiceEntry {
  position: number;
}

type FgdsServiceFormValue = Record<string, boolean | string | number | null | undefined>;

export const FGDS_SERVICE_DEFINITIONS: FgdsServiceDefinition[] = [
  {
    key: "assembly",
    label: () => $localize`:@@global.assembly:Montage`,
    partNumber: "1965124",
  },
  {
    key: "installation",
    label: () => $localize`:@@global.installation:Inbetriebnahme`,
    partNumber: "1947222",
  },
  {
    key: "documentation",
    label: () => $localize`:@@global.documentation:Dokumentation`,
    partNumber: "3721413",
  },
  {
    key: "engineering",
    label: () => $localize`:@@global.engineering:Engineering`,
    partNumber: "1907670",
  },
  {
    key: "additionalServices",
    label: () => $localize`:@@global.additionalServices:Sonstige Dienstleistungen`,
    partNumber: "",
  },
];

export function getFgdsProjectService(project: FgdsProject, key: FgdsServiceKey): ProjectRelatedService {
  switch (key) {
    case "assembly":
      return project.assembly;
    case "installation":
      return project.installation;
    case "documentation":
      return project.documentation;
    case "engineering":
      return project.engineering;
    case "additionalServices":
      return project.additionalServices;
  }
}

export function getFgdsProjectServices(project: FgdsProject): FgdsProjectServiceEntry[] {
  return FGDS_SERVICE_DEFINITIONS.map((definition) => ({
    definition,
    service: getFgdsProjectService(project, definition.key),
  }));
}

export function getRequiredFgdsProjectServices(project: FgdsProject): FgdsProjectServiceEntry[] {
  return getFgdsProjectServices(project).filter(({ service }) => service.required);
}

export function getRequiredFgdsProjectServicesWithPositions(
  project: FgdsProject,
  latestProductPositionId: number,
): PositionedFgdsProjectServiceEntry[] {
  let position = latestProductPositionId + 1;
  return getRequiredFgdsProjectServices(project).map((entry) => ({
    ...entry,
    position: position++,
  }));
}

export function getFgdsNotesControlName(key: FgdsServiceKey) {
  return `${key}Notes`;
}

export function getFgdsCostsControlName(key: FgdsServiceKey) {
  return `${key}Costs`;
}

export function getFgdsCheckboxTestId(key: FgdsServiceKey) {
  return `productList.checkbox${capitalizeFgdsServiceKey(key)}`;
}

export function getFgdsNotesTestId(key: FgdsServiceKey) {
  return `productList.textarea${capitalizeFgdsServiceKey(key)}`;
}

export function getFgdsPriceTestId(key: FgdsServiceKey) {
  return `productList.input${capitalizeFgdsServiceKey(key)}`;
}

export function createFgdsServicesFormValue(project: FgdsProject): FgdsServiceFormValue {
  return Object.fromEntries(
    getFgdsProjectServices(project).flatMap(({ definition, service }) => [
      [definition.key, service.required],
      [getFgdsCostsControlName(definition.key), service.costs],
      [getFgdsNotesControlName(definition.key), service.notes],
    ]),
  );
}

export function mapFgdsServicesFormValue(project: FgdsProject, value: FgdsServiceFormValue) {
  getFgdsProjectServices(project).forEach(({ definition, service }) => {
    service.required = value[definition.key] === true;
    const costs = value[getFgdsCostsControlName(definition.key)];
    const notes = value[getFgdsNotesControlName(definition.key)];
    service.costs = typeof costs === "number" ? costs : undefined;
    service.notes = typeof notes === "string" ? notes : "";
  });
}

function capitalizeFgdsServiceKey(key: FgdsServiceKey) {
  return key.charAt(0).toUpperCase() + key.slice(1);
}
